/**
 * Feed-filter edge conversions — the single place that translates the
 * SortFilterBar's UI-native filter values into the SDK query params for the
 * Markets / Winners / Proposals discovery feeds, AND round-trips them through
 * the URL so a filtered feed is shareable.
 *
 * UNITS (verified against the backend schemas on 2026-06-01):
 *  - money params (`volumeMin/Max`, `prizePoolMin/Max`, `liquidityMin/Max`,
 *    `stakedMin/Max`, `marketCapMin/Max`) are **D6 USDC strings**. The sliders
 *    carry whole-dollar display values → multiply by 1e6 and round.
 *  - `change24hMin/MaxBps` are **signed basis points** — display % → `pct*100`.
 *  - `fundingMin/Max` are **0–100 percent integers** (the proposals service
 *    compares `prizePool*100/threshold` directly), so the slider value passes
 *    through unscaled.
 *  - int/count params (`backers*`, `traders*`, `outcomes*`, `holders*`) pass
 *    through as plain integer strings.
 *  - date params (`createdAfter/Before`, `graduatedAfter/Before`) are **ISO
 *    datetime strings**, resolved from the date-slider's percent positions via
 *    `percentToDate(...).toISOString()`.
 *  - multi params (`category`, `location`) are **CSV** strings.
 *  - `creator` is a **CSV of wallet addresses**.
 *
 * A param is only emitted when its thumb has moved off the extreme (a slider
 * not at `[min,max]`, a date slider not at `[0,100]`, a non-empty multi). When
 * "Any", the param is omitted so the backend's unfiltered branch runs.
 */

import { percentToDate } from '@/components/ui/date-range-slider-format';
import type { MultiSelectOption } from '@/components/ui/searchable-multi-select';
import { D6_SCALE } from '@/config/solana';

const EPSILON = 1e-6;

/** A `[min, max]` slider range in the dimension's native (display) units. */
export type Range = [number, number];

/** The outcomes-slider bounds — BOTH ends sourced from PlatformConfig
 *  (`minOutcomes`/`maxOutcomes`), shared by the markets + proposals feeds. */
export interface OutcomesBounds {
  min: number;
  max: number;
}

/** Convert whole-dollar display value → D6 USDC integer string. */
export function dollarsToD6(value: number): string {
  return String(Math.round(value * D6_SCALE));
}

/** Convert a display percent → signed basis-point integer string. */
export function pctToBps(value: number): string {
  return String(Math.round(value * 100));
}

/** Plain integer string for int/count params. */
export function intString(value: number): string {
  return String(Math.round(value));
}

function atMin(lo: number, min: number): boolean {
  return lo <= min + EPSILON;
}
function atMax(hi: number, max: number): boolean {
  return hi >= max - EPSILON;
}

/**
 * Emit `{ <minKey>?, <maxKey>? }` for a numeric range, applying `encode` to
 * each endpoint. Endpoints sitting at their extreme are omitted (open-ended).
 */
export function rangeParams(
  value: Range | undefined,
  min: number,
  max: number,
  minKey: string,
  maxKey: string,
  encode: (v: number) => string,
): Record<string, string> {
  if (value === undefined) return {};
  const [lo, hi] = value;
  const out: Record<string, string> = {};
  if (!atMin(lo, min)) out[minKey] = encode(lo);
  if (!atMax(hi, max)) out[maxKey] = encode(hi);
  return out;
}

/**
 * Emit `{ <afterKey>?, <beforeKey>? }` ISO timestamps for a date-slider range.
 * The slider stores `[loPercent, hiPercent]` (0 = `spanDays` ago, 100 = now);
 * `lo` resolves to "after", `hi` to "before".
 */
export function dateRangeParams(
  value: Range | undefined,
  spanDays: number,
  now: number,
  afterKey: string,
  beforeKey: string,
): Record<string, string> {
  if (value === undefined) return {};
  const [loPct, hiPct] = value;
  const out: Record<string, string> = {};
  if (loPct > EPSILON) {
    out[afterKey] = percentToDate(loPct, spanDays, now).toISOString();
  }
  if (hiPct < 100 - EPSILON) {
    out[beforeKey] = percentToDate(hiPct, spanDays, now).toISOString();
  }
  return out;
}

/** Join a string[] (category/location chips) into a CSV, or omit when empty. */
export function csvParam(key: string, values: string[] | undefined): Record<string, string> {
  if (values === undefined || values.length === 0) return {};
  return { [key]: values.join(',') };
}

/** Join selected creator options into a CSV of their `value` (wallet address). */
export function creatorParam(
  key: string,
  selected: MultiSelectOption[] | undefined,
): Record<string, string> {
  if (selected === undefined || selected.length === 0) return {};
  return { [key]: selected.map((o) => o.value).join(',') };
}

// ───────────────────────────────────────────────────────────────────────────
// URL round-trip — sliders/dates/multis serialise compactly so the feed URL
// stays shareable. Encoding is deliberately the SAME shape as the SDK param
// (so the wire and the URL never diverge): ranges as `lo-hi` in native units,
// dates as `lo-hi` percent, multis as CSV.
// ───────────────────────────────────────────────────────────────────────────

// `~` separates the endpoints — it is URL-safe (RFC 3986 unreserved) and, unlike
// `-`, can never appear inside a number, so a fully-negative signed range (e.g.
// the 24h-price-change axis `[-50, -10]`) round-trips. A `-` separator turned
// `"-50--10"` into an unparseable 4-part split and silently reset the filter.
const RANGE_SEP = '~';

/** Serialise a `[lo, hi]` range to `"lo~hi"`, or null when at the extremes. */
export function rangeToParam(value: Range, min: number, max: number): string | null {
  const [lo, hi] = value;
  if (atMin(lo, min) && atMax(hi, max)) return null;
  return `${lo}${RANGE_SEP}${hi}`;
}

/** Parse a `"lo~hi"` URL value back to a `[lo, hi]` range, clamped to bounds. */
export function paramToRange(raw: string | null, min: number, max: number): Range {
  if (raw === null) return [min, max];
  const parts = raw.split(RANGE_SEP);
  if (parts.length !== 2) return [min, max];
  const lo = Number(parts[0]);
  const hi = Number(parts[1]);
  if (Number.isNaN(lo) || Number.isNaN(hi)) return [min, max];
  return [Math.max(min, Math.min(lo, hi)), Math.min(max, Math.max(lo, hi))];
}

/** Serialise a date-slider `[loPct, hiPct]` to `"loPct-hiPct"`, or null when full. */
export function dateRangeToParam(value: Range): string | null {
  const [loPct, hiPct] = value;
  if (loPct <= EPSILON && hiPct >= 100 - EPSILON) return null;
  return `${loPct}-${hiPct}`;
}

/** Parse a date `"loPct-hiPct"` URL value, clamped to 0–100. */
export function paramToDateRange(raw: string | null): Range {
  if (raw === null) return [0, 100];
  const parts = raw.split('-');
  if (parts.length !== 2) return [0, 100];
  const lo = Number(parts[0]);
  const hi = Number(parts[1]);
  if (Number.isNaN(lo) || Number.isNaN(hi)) return [0, 100];
  return [Math.max(0, Math.min(lo, 100)), Math.max(0, Math.min(hi, 100))];
}

/** Parse a CSV URL value into a validated string[] (drops unknown tokens). */
export function paramToCsv(raw: string | null, valid: ReadonlySet<string>): string[] {
  if (raw === null || raw.length === 0) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && valid.has(s));
}
